import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

/* ─────────────────────────────────────────────────────────────
   CONFIGURATION — mirrors $.GridRotator.defaults exactly
   ───────────────────────────────────────────────────────────── */
const ROWS          = 3;
const COLUMNS       = 8;
const VISIBLE_COUNT = ROWS * COLUMNS; // 24
const MAX_STEP      = 3;              // step:'random', maxStep:3
const ANIM_SPEED    = 800;            // ms
const INTERVAL      = 3000;          // ms
const EASING        = 'linear';       // animEasingIn / animEasingOut

const PHOTOS = [
  'Amey Navle.jpeg',
  'Akshay Agarwal.jpg',
  'Amit Dhulekar.jpg',
  'Anuja Deshmukh.jpg',
  'Balkrishna Kadam.jpg',
  'Hital Shah.jpg',
  'Ishani Toradmal.jpg',
  'Kartik Shinde.jpg',
  'Nainish Munot.jpg',
  'Prafull Aher.jpg',
  'Prasenjeet Anil Baisane Photo.jpg',
  'Prathamesh Mahajan.jpg',
  'Purva More.jpg',
  'Purva Vaidya.jpg',
  'Ramvilas Kendre.jpg',
  'Rutuja Patil.png',
  'Rutuja Rachkar.jpg',
  'Saket Thombre.jpg',
  'Shweta Lokhande.jpg',
  'Siddharth More.jpg',
  'Sourabh Tupe.jpeg',
  'Suhas Mohol.jpg',
  'Suhrud Kulkarni.JPG',
  'Vicky Punjabi.jpg',
];

/* ─────────────────────────────────────────────────────────────
   ANIMATION TYPES — exact list from the plugin (3-D branch)
   ───────────────────────────────────────────────────────────── */
const ANIM_TYPES = [
  'fadeInOut',
  'slideLeft',
  'slideRight',
  'slideTop',
  'slideBottom',
  'rotateLeft',
  'rotateRight',
  'rotateTop',
  'rotateBottom',
  'scale',
  'rotate3d',
  'rotateLeftScale',
  'rotateRightScale',
  'rotateTopScale',
  'rotateBottomScale',
];

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */
const randomInt = (max) => Math.floor(Math.random() * max);

const shuffleArray = (arr) => {
  const a = [...arr];
  let i = a.length;
  while (i--) {
    const p = Math.floor(Math.random() * i);
    [a[i], a[p]] = [a[p], a[i]];
  }
  return a;
};

/**
 * Direct port of _getAnimProperties from the plugin.
 * Returns start/end CSS props for both incoming and outgoing elements.
 */
const getAnimProperties = (animType, tileW, tileH) => {
  let startInProp  = {};
  let startOutProp = {};
  let endInProp    = {};
  let endOutProp   = {};
  let speed;
  let delay = 0;

  switch (animType) {
    case 'fadeInOut':
      endOutProp.opacity = '0';
      break;

    case 'slideLeft':
      startInProp.left = `${tileW}px`;
      endInProp.left   = '0px';
      endOutProp.left  = `-${tileW}px`;
      break;

    case 'slideRight':
      startInProp.left = `-${tileW}px`;
      endInProp.left   = '0px';
      endOutProp.left  = `${tileW}px`;
      break;

    case 'slideTop':
      startInProp.top = `${tileH}px`;
      endInProp.top   = '0px';
      endOutProp.top  = `-${tileH}px`;
      break;

    case 'slideBottom':
      startInProp.top = `-${tileH}px`;
      endInProp.top   = '0px';
      endOutProp.top  = `${tileH}px`;
      break;

    case 'rotateLeft':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'rotateY(90deg)';
      endInProp.transform    = 'rotateY(0deg)';
      delay = speed;
      endOutProp.transform   = 'rotateY(-90deg)';
      break;

    case 'rotateRight':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'rotateY(-90deg)';
      endInProp.transform    = 'rotateY(0deg)';
      delay = speed;
      endOutProp.transform   = 'rotateY(90deg)';
      break;

    case 'rotateTop':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'rotateX(90deg)';
      endInProp.transform    = 'rotateX(0deg)';
      delay = speed;
      endOutProp.transform   = 'rotateX(-90deg)';
      break;

    case 'rotateBottom':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'rotateX(-90deg)';
      endInProp.transform    = 'rotateX(0deg)';
      delay = speed;
      endOutProp.transform   = 'rotateX(90deg)';
      break;

    case 'scale':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'scale(0)';
      startOutProp.transform = 'scale(1)';
      endInProp.transform    = 'scale(1)';
      delay = speed;
      endOutProp.transform   = 'scale(0)';
      break;

    case 'rotateLeftScale':
      speed = ANIM_SPEED / 2;
      startOutProp.transform = 'scale(1)';
      startInProp.transform  = 'scale(0.3) rotateY(90deg)';
      endInProp.transform    = 'scale(1) rotateY(0deg)';
      delay = speed;
      endOutProp.transform   = 'scale(0.3) rotateY(-90deg)';
      break;

    case 'rotateRightScale':
      speed = ANIM_SPEED / 2;
      startOutProp.transform = 'scale(1)';
      startInProp.transform  = 'scale(0.3) rotateY(-90deg)';
      endInProp.transform    = 'scale(1) rotateY(0deg)';
      delay = speed;
      endOutProp.transform   = 'scale(0.3) rotateY(90deg)';
      break;

    case 'rotateTopScale':
      speed = ANIM_SPEED / 2;
      startOutProp.transform = 'scale(1)';
      startInProp.transform  = 'scale(0.3) rotateX(90deg)';
      endInProp.transform    = 'scale(1) rotateX(0deg)';
      delay = speed;
      endOutProp.transform   = 'scale(0.3) rotateX(-90deg)';
      break;

    case 'rotateBottomScale':
      speed = ANIM_SPEED / 2;
      startOutProp.transform = 'scale(1)';
      startInProp.transform  = 'scale(0.3) rotateX(-90deg)';
      endInProp.transform    = 'scale(1) rotateX(0deg)';
      delay = speed;
      endOutProp.transform   = 'scale(0.3) rotateX(90deg)';
      break;

    case 'rotate3d':
      speed = ANIM_SPEED / 2;
      startInProp.transform  = 'rotate3d(1, 1, 0, 90deg)';
      endInProp.transform    = 'rotate3d(1, 1, 0, 0deg)';
      delay = speed;
      endOutProp.transform   = 'rotate3d(1, 1, 0, -90deg)';
      break;

    default:
      endOutProp.opacity = '0';
      break;
  }

  return {
    startInProp,
    startOutProp,
    endInProp,
    endOutProp,
    delay,
    animSpeed: speed !== undefined ? speed : ANIM_SPEED,
  };
};

/* Apply an object of CSS props directly to a DOM element */
const applyCSS = (el, props) => {
  if (!el) return;
  Object.assign(el.style, props);
};

/* ─────────────────────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function StudentsJoinSection() {
  /*
   * currentImages[i] — the filename currently shown in slot i.
   * This is the only React state we need; it drives the
   * background-image of the OUTGOING element on the next render.
   *
   * All animation is driven imperatively through refs.
   */
  const [currentImages, setCurrentImages] = useState(
    () => [...PHOTOS.slice(0, VISIBLE_COUNT)]
  );

  /*
   * outItems — FIFO queue (mirrors plugin's this.outItems).
   * Starts empty because all 24 photos are already visible.
   * Outgoing photos are pushed here; they are pulled as incoming
   * images on the next replacement cycle.
   */
  const outItemsRef = useRef([]);

  /*
   * Tracks which tile indexes are currently mid-animation.
   * Mirrors plugin's $item.data('active').
   */
  const activeRef = useRef(new Set());

  /*
   * DOM refs for every tile's two <a> elements.
   * inRefs[i]  = the incoming (top, z-index 2) <a>
   * outRefs[i] = the outgoing (bottom, z-index 1) <a>
   */
  const inRefs  = useRef(Array(VISIBLE_COUNT).fill(null));
  const outRefs = useRef(Array(VISIBLE_COUNT).fill(null));

  /*
   * currentImagesRef mirrors currentImages so the interval
   * callback can read the latest values without a stale closure.
   */
  const currentImagesRef = useRef([...PHOTOS.slice(0, VISIBLE_COUNT)]);

  useEffect(() => {
    /* ── replace(idx) — port of plugin's _replace($out) ── */
    const replace = (idx) => {
      activeRef.current.add(idx);

      const outgoing = currentImagesRef.current[idx];

      /* Pull next incoming from the FIFO queue */
      let incoming = outItemsRef.current.shift();

      /* If queue is empty or we'd repeat the same image, recycle */
      if (!incoming || incoming === outgoing) {
        /* Find any photo not currently visible */
        const visible = new Set(currentImagesRef.current);
        incoming = PHOTOS.find((p) => !visible.has(p)) ?? outgoing;
      }

      /* Push outgoing to end of queue (FIFO) */
      outItemsRef.current.push(outgoing);

      /* ── KEY FIX: set background-image directly on the DOM
         element BEFORE starting any animation.  This is what
         the jQuery plugin does — it sets background-image on
         the anchor element imperatively, completely bypassing
         React's render cycle. ── */
      const inEl  = inRefs.current[idx];
      const outEl = outRefs.current[idx];

      if (!inEl || !outEl) {
        activeRef.current.delete(idx);
        return;
      }

      /* Set incoming image directly — no waiting for React */
      inEl.style.backgroundImage = `url("/images/testimonials/${incoming}")`;
      /* Make sure incoming is visible (opacity may have been 0) */
      inEl.style.opacity  = '1';
      inEl.style.display  = 'block';

      /* Measure tile dimensions for slide offsets */
      const tileEl = outEl.parentElement;
      const tileW  = tileEl ? tileEl.offsetWidth  : 100;
      const tileH  = tileEl ? tileEl.offsetHeight : 100;

      const animType = ANIM_TYPES[randomInt(ANIM_TYPES.length)];
      const {
        startInProp,
        startOutProp,
        endInProp,
        endOutProp,
        delay,
        animSpeed,
      } = getAnimProperties(animType, tileW, tileH);

      /* ── Step 1: apply start transforms instantly (no transition) ── */
      inEl.style.transition  = 'none';
      outEl.style.transition = 'none';
      applyCSS(inEl,  startInProp);
      applyCSS(outEl, startOutProp);

      /* ── Step 2 (25 ms): set transition property ── */
      const t1 = setTimeout(() => {
        inEl.style.transition  = `all ${animSpeed}ms ${delay}ms ${EASING}`;
        outEl.style.transition = `all ${animSpeed}ms 0ms ${EASING}`;

        /* ── Step 3 (another 25 ms): apply end state → triggers animation ── */
        const t2 = setTimeout(() => {
          applyCSS(inEl,  endInProp);
          applyCSS(outEl, endOutProp);

          /* ── Step 4: after animation completes, commit new image ── */
          const totalDuration = delay + animSpeed + 60;

          const t3 = setTimeout(() => {
            /*
             * Commit: make the outgoing element show the incoming
             * image so future animations use the correct photo.
             * Do this directly on the DOM element to avoid a
             * React re-render race.
             */
            outEl.style.transition      = 'none';
            outEl.style.backgroundImage = `url("/images/testimonials/${incoming}")`;
            outEl.style.transform       = '';
            outEl.style.opacity         = '';
            outEl.style.left            = '';
            outEl.style.top             = '';

            /* Reset incoming element back to invisible/neutral */
            inEl.style.transition      = 'none';
            inEl.style.backgroundImage = 'none';
            inEl.style.transform       = '';
            inEl.style.opacity         = '0';
            inEl.style.left            = '';
            inEl.style.top             = '';

            /* Update React state + ref so next cycle reads the correct photo */
            currentImagesRef.current[idx] = incoming;
            setCurrentImages((prev) => {
              const next = [...prev];
              next[idx]  = incoming;
              return next;
            });

            activeRef.current.delete(idx);
          }, totalDuration);

          return () => clearTimeout(t3);
        }, 25);

        return () => clearTimeout(t2);
      }, 25);

      return () => clearTimeout(t1);
    };

    /* ── showNext — port of plugin's _showNext ── */
    const showNext = () => {
      const nmbOut  = randomInt(MAX_STEP) + 1; // 1..maxStep
      const indexes = shuffleArray(
        Array.from({ length: VISIBLE_COUNT }, (_, i) => i)
      );

      let replaced = 0;
      for (const idx of indexes) {
        if (replaced >= nmbOut) break;
        if (activeRef.current.has(idx)) continue;
        replace(idx);
        replaced++;
      }
    };

    const intervalId = setInterval(showNext, INTERVAL);
    return () => clearInterval(intervalId);
  }, []); // runs once

  /* ── RENDER ─────────────────────────────────────────────── */
  return (
    <section className="sa-students-join">

      <div className="sa-join-inner">

        <div className="ri-grid ri-grid-size-3">
          <ul className="ri-grid-list">
            {currentImages.map((photo, idx) => (
              <li key={idx} className="ri-grid-item">

                {/*
                  * INCOMING — sits on top (z-index 2).
                  * background-image is set imperatively by replace().
                  * Starts with opacity:0 and backgroundImage:none so it
                  * is invisible until replace() populates it.
                  */}
                <a
                  ref={(el) => { inRefs.current[idx] = el; }}
                  className="ri-grid-in"
                  style={{ opacity: 0, backgroundImage: 'none', pointerEvents: 'none' }}
                  aria-hidden="true"
                />

                {/*
                  * OUTGOING / current — sits below (z-index 1).
                  * background-image is set by React state on mount,
                  * then updated imperatively by replace() after each swap.
                  */}
                <a
                  ref={(el) => { outRefs.current[idx] = el; }}
                  className="ri-grid-out"
                  style={{
                    backgroundImage: `url("/images/testimonials/${photo}")`,
                    pointerEvents: 'none',
                  }}
                  aria-hidden="true"
                />

              </li>
            ))}
          </ul>
        </div>

        <div className="sa-join-cta">
          <div className="sa-join-cta-inner">
            <h2>Join <span>50,000</span> Students.</h2>
            <Link to="/login" className="sa-btn">Register</Link>
          </div>
        </div>

      </div>

    </section>
  );
}
